<template>
    <div
        class="company-side-nav display-flex flex-column justify-between"
        :class="{ collapsed: isCollapsed }"
    >
        <div class="top display-flex flex-column pt-lg">
            <div class="collapse-button" @click="toggleCollapse">
                <font-awesome-icon
                    :icon="`fa-arrow-left-to-line`"
                    size="lg"
                    :class="{ rotated: isCollapsed }"
                    class="text-stone-700"
                />
            </div>
            <div class="company-info px-lg">
                <div class="info-content">
                    <p class="large font-bold text-truncate text-stone-1000" style="width: 75%">
                        {{ entityStore.entity?.name }}
                    </p>
                    <p class="extra-small text-stone-800">
                        Parent: {{ entityStore.entity?.ultimate_aon_entity_name }}
                    </p>
                    <div
                        class="ticker-and-actions display-flex align-items-center justify-between mt-xs"
                    >
                        <div class="ticker-exchange display-flex align-items-center">
                            <div
                                v-if="entityStore.entity?.stock_ticker"
                                class="ticker bg-stone-300 p-xs"
                            >
                                <p class="extra-small">
                                    {{ entityStore.entity?.stock_ticker }}
                                </p>
                            </div>
                            <div
                                v-if="entityStore.entity?.stock_ticker"
                                class="country-flag ml-sm mr-xs"
                                :class="countryFlag"
                            ></div>
                            <p
                                v-if="entityStore.entity?.stock_exchange"
                                class="extra-small"
                                style="padding-top: 1px"
                            >
                                {{ entityStore.entity?.stock_exchange }}
                            </p>
                        </div>
                        <div class="simple-actions display-flex align-items-center">
                            <font-awesome-icon
                                v-tooltip="{
                                    content: 'View Company Hierarchy',
                                    delay: { show: 500, hide: 100 },
                                }"
                                class="heirarchy-icon text-stone-600 mr-sm cursor-pointer"
                                :icon="`fas fa-sitemap`"
                                @click="showCompanyHierarchy = true"
                            />
                            <AddToWatchlistButton
                                v-tooltip="{
                                    content: 'Add to Watchlist',
                                    delay: { show: 500, hide: 100 },
                                }"
                                use-new-icon
                                :key="entityStore.entity?.aon_entity_pk"
                                :id-values="[entityStore.entity?.aon_entity_pk]"
                                id-type="Entity"
                                type="prominent"
                            />
                        </div>
                    </div>
                </div>
                <div class="separation-line my-xl w-100"></div>
            </div>
            <div class="company-tabs px-lg">
                <ArmoryStackedTabs
                    :tabs="tabData"
                    :current-tab="entityStore.getCurrentCompanyTab"
                    :current-sub-tab="$route.query.subTabName"
                    :collapsed="isCollapsed"
                    @tab-change="tabChanged"
                />
            </div>
        </div>
        <div class="bottom pb-sm">
            <div class="separation-line-holder px-xl">
                <div class="separation-line my-xl w-100"></div>
            </div>
            <div class="company-pro-tabs px-sm">
                <ArmoryStackedTabs
                    :tabs="proTabData"
                    :current-tab="entityStore.getCurrentCompanyTab"
                    :current-sub-tab="$route.query.subTabName"
                    :collapsed="isCollapsed"
                    @tab-change="tabChanged"
                />
            </div>
        </div>
    </div>
    <CompanyHierarchy
        v-if="showCompanyHierarchy"
        :parent-entity-id="parentEntityId"
        :selected-entity-id="parseInt(entityStore.entity?.aon_entity_pk)"
        @cancel="showCompanyHierarchy = false"
    />
</template>

<script setup>
import { ref, computed, inject, onMounted } from 'vue'
import { useAuth } from '@/auth/authPlugin'

import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useEntityStore, useMoat2ProductStore, useReportGenerationStore } from '@/stores'
import { useFlag } from '@unleash/proxy-client-vue'

import CompanyHierarchy from '@/components/company/CompanyHierarchy'
import AddToWatchlistButton from '../Watchlists/WatchlistActions/AddToWatchlistButton'
import ArmoryStackedTabs from '../helpers/ArmoryStackedTabs.vue'

const router = useRouter()
const { t } = useI18n()
const { user, isAuthenticated, logout } = useAuth()

const entityStore = useEntityStore()
const moat2ProductStore = useMoat2ProductStore()
const reportGenerationStore = useReportGenerationStore()

// flags
const showInnovationTab = useFlag('alpha.innovationTab')
const isProUser = useFlag('ipAlpha.proUsers')
const portfolioFeatureWhitelist = useFlag('ipAlpha.portfolioFeaturesWhitelist')
const allowAllFeatures = useFlag('ipAlpha.godAccess')
const bottomUpProductsEnabled = useFlag('ipAlpha.bottomUpProducts')
const benchmarkingEnabled = useFlag('ipAlpha.benchmarkingTab')

const showCompanyHierarchy = ref(false)
const tabUpdater = ref(0)
const generateLoading = ref(false)
const isCollapsed = ref(false)

const parentEntityId = computed(() => {
    return entityStore.entity.ultimate_aon_entity_pk
        ? parseInt(entityStore.entity.ultimate_aon_entity_pk)
        : parseInt(entityStore.entity.aon_entity_pk)
})

const countryFlag = computed(() => {
    if (!entityStore.entity.stock_ticker || !entityStore.entity.stock_region) {
        return ''
    }
    return `flag:${entityStore.entity.stock_region}`
})

const allowPortfolioToBeVisible = computed(() => {
    if (portfolioFeatureWhitelist.value || allowAllFeatures.value) {
        return true
    }

    if (
        isProUser.value &&
        parseInt(user.value.entity_pk) === parseInt(entityStore.entity?.aon_entity_pk)
    ) {
        return true
    }

    return false
})

const tabData = computed(() => {
    let finalTabList = []
    finalTabList.push(
        {
            label: 'Insights',
            value: 'profile',
            icon: 'fa-circle-info',
        },
        {
            label: 'Strategy',
            value: 'strategy',
            icon: 'fa-lightbulb-on',
            subTabs: [
                {
                    label: 'Innovation',
                    value: 'innovation',
                    icon: 'fa-atom-simple',
                },
                {
                    label: 'Talent',
                    value: 'talent',
                    icon: 'fa-user',
                },
            ],
        },
        {
            label: 'Risk',
            value: 'risk',
            icon: 'fa-triangle-exclamation',
        },
        {
            label: 'Value',
            value: 'value',
            icon: 'fa-circle-dollar',
        },
        {
            label: 'IP Basics',
            value: 'ipBasics',
            icon: 'fa-coffee-pot',
        }
    )

    if (showInnovationTab.value) {
        finalTabList.push({
            label: 'Innovation',
            value: 'innovation',
            icon: 'fa-atom-simple',
        })
    }

    if (bottomUpProductsEnabled.value) {
        finalTabList.push({
            label: 'Bottom Up Products',
            value: 'bottomUpProducts',
            icon: 'fa-cubes-stacked',
        })
    }

    if (benchmarkingEnabled.value) {
        finalTabList.splice(2, 0, {
            label: 'Benchmarking',
            value: 'benchmarking',
            icon: 'fa-ruler',
        })
    } else {
        finalTabList.splice(2, 0, {
            label: 'Moat2Product',
            value: 'moat2Product',
            icon: 'fa-microchip',
        })
    }

    return finalTabList
})

const proTabData = computed(() => {
    let finalTabList = []

    if (isProUser.value) {
        finalTabList.push({
            label: 'Patents',
            value: 'patentList',
            icon: 'fa-table-list',
            pro: true,
        })
    }

    if (allowPortfolioToBeVisible.value) {
        finalTabList.push({
            label: 'Portfolio Manager',
            value: 'portfolioManager',
            icon: 'fa-gear',
            pro: true,
        })
    }

    finalTabList.push({
        label: 'Generate Report',
        value: 'generateReport',
        icon: 'fa-diagram-successor',
        onClick: gotoReportGenerator,
    })

    return finalTabList
})

const tabChanged = (tab, subTab) => {
    let query = {
        tabName: tab.value,
    }

    if (tab.subTabs) {
        if (!subTab) {
            query.subTabName = tab.subTabs[0].value
        } else {
            query.subTabName = subTab.value
        }
    }

    router.push({
        name: 'Research Company',
        params: { targetPk: entityStore.entity.aon_entity_pk },
        query: query,
    })
}

const gotoReportGenerator = async () => {
    generateLoading.value = true

    reportGenerationStore.targetCompany = entityStore.entity.name
    router.push({ name: 'Create Report', query: { source: 'pa' } })
}

const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
}
</script>

<style lang="scss" scoped>
.company-side-nav {
    width: 300px;
    height: calc(100vh);
    position: relative;
    position: sticky;
    top: 0;
    border-right: 1px solid var(--color-stone-300);
    transition: all 0.3s ease-in-out;

    &.collapsed {
        width: 70px;
        .company-info {
            .info-content {
                opacity: 0;
                // max-height: 10px;
                margin: 0;
                padding: 0;
                pointer-events: none;
            }
        }

        .company-tabs {
            padding-left: 12px !important;
            padding-right: 12px !important;
        }
    }

    .company-info {
        .info-content {
            transition: all 0.3s ease-in-out;
            opacity: 1;
            max-height: 80px;
            overflow: hidden;
        }
    }

    .collapse-button {
        position: absolute;
        top: 20px;
        right: 25px;
        cursor: pointer;
        transition: all 0.3s ease-in-out;

        .rotated {
            transform: rotate(180deg);
        }
    }

    .separation-line {
        height: 1px;
        background: var(--color-stone-300);
    }

    .company-tabs {
        overflow-y: auto;
        max-height: calc(100vh - 400px);
    }

    .bottom {
        position: sticky;
        bottom: 0;
        left: 0;
    }

    .ticker-and-actions {
        .ticker {
            border-radius: var(--border-radius-md);
        }

        p {
            line-height: var(--line-height-tight);
        }
    }

    .country-flag {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        position: relative;
    }
}
</style>
